import { call, put, select, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';

import * as BreakdownActions from '../actions';
import { makeBackendApi } from 'services/BackendApi';
import { bookingCurrencySymbolSelector } from 'store/modules/bookingManager/selectors';
import { ICurrencyExchangeQuery, ICurrencyExchangeResponse } from 'services/BackendApi/types/CurrencyExchange';
import { getCurrencyBySymbol } from 'utils';
import { getExchangeCurrencyFailureAction, getExchangeCurrencySuccessAction } from '../actions';
import { exchangeCurrencyCodeSelector } from '../selectors';
import { TCurrencyCode } from 'interfaces';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { ISystemExchangeRateListResponse } from 'containers/ConversionRates/types';

function* fetchExchangeRate(toCurrencyCode: TCurrencyCode) {
  try {
    const backendApi = makeBackendApi();
    const bookingCurrencySymbol = yield select(bookingCurrencySymbolSelector);
    const fromCurrency = getCurrencyBySymbol(bookingCurrencySymbol).code;

    const dynamicParameters = ParameterService.getParameters();
    const useSystemExchangeRate = dynamicParameters.ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP;

    if (useSystemExchangeRate) {
      const result: AxiosResponse<ISystemExchangeRateListResponse> = yield call(
        backendApi.getSystemExchangeRates,
        { page: 1, limit: 1, sortBy: 'currency', sortDir: 'asc', from: fromCurrency, to: toCurrencyCode }
      );
      const rateData = result.data.data[0];
      if (!rateData) {
        yield put(getExchangeCurrencyFailureAction());
        return;
      }
      yield put(getExchangeCurrencySuccessAction(rateData.effectiveSell, new Date().toISOString()));
    } else {
      const query: ICurrencyExchangeQuery = { from: fromCurrency, to: toCurrencyCode };
      const result: AxiosResponse<ICurrencyExchangeResponse> = yield call(backendApi.getExchangeRate, query);
      yield put(getExchangeCurrencySuccessAction(result.data.data.exchangeRate, result.data.data.timestamp));
    }
  } catch {
    yield put(getExchangeCurrencyFailureAction());
  }
}

export function* getExchangeRateSaga(action: BreakdownActions.GetExchangeCurrencyRequestAction) {
  yield* fetchExchangeRate(action.currencyCode);
}

export function* refreshExchangeRateSaga() {
  const exchangeCurrencyCode = yield select(exchangeCurrencyCodeSelector);
  yield* fetchExchangeRate(exchangeCurrencyCode);
}

export function* watchGetExchangeRateSaga() {
  yield takeLatest(
    [BreakdownActions.GET_EXCHANGE_CURRENCY_REQUEST],
    getExchangeRateSaga
  );
  yield takeLatest(
    [BreakdownActions.REFRESH_EXCHANGE_CURRENCY],
    refreshExchangeRateSaga
  );
}
